import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import Typography from "../common-components/Typography";
import content from "../constants/content";
import vmTheme from "../constants/theme";
import { font } from "../constants/font";
import { STATIC_PATH } from "../constants/config";
import Action from "../common-components/Action";
import { Celebration, ExportSidebar } from "./publish-component";
import { isPaidUser } from "../helper/checkUser";
import ExportDetails from "./export-details";
import Youtube from "./youtube";
import Tiktok from "./tiktok";
import Progress from "./progress";
import { setStageClarity } from "../helper/trackClarity";
import TRACKERS from "../constants/trackers";
import useExport from "../helper/hooks/useExport";
import useWatermark from "../helper/hooks/useWatermark";
import useUpgrade from "../helper/hooks/useUpgrade";
import Exceed from "./Exceed";

const Publish = ({ isShortsPlayer, shortId, isPublish }) => {
  const history = useHistory();
  const location = useLocation();
  const userDetails = useSelector((store) => store.userDetails);
  const theme = useSelector((state) => state.app.get("theme"));
  const isPaid = isPaidUser(userDetails.plan);
  const { credits } = useSelector((state) => state.userDetails);
  const { totalCredits, usedCredits } = credits.socialpublishcount || {};
  const { totalCredits: exportTotalCredits, usedCredits: exportUsedCredits } = credits.exportcount || {};
  const [remainingCredits, setRemainingCredits] = useState({
    social: 0,
    export: 0
  });
  const [vcTriggered, setVcTriggered] = useState(false);
  const { triggerVC } = useExport();
  const { projectId } = useSelector((state) => state.userDetails);
  const { showPricingModal } = useUpgrade();

  const showWatermMark = useWatermark();

  const [isYoutube, setIsYoutube] = useState(false);
  const [isDownload, setIsDownload] = useState(false);
  const [isTikTok, setIsTikTok] = useState(false);

  useEffect(() => {
    setRemainingCredits({
      social: totalCredits - usedCredits,
      export: exportTotalCredits - exportUsedCredits
    })
  }, [totalCredits, usedCredits, exportTotalCredits, exportUsedCredits])

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const exportType = params.get("export_type");
    // To maintain the state on page refresh.
    if (exportType) {
      setIsDownload(exportType === "download");
      setIsYoutube(exportType === "youtube");
      setIsTikTok(exportType === "tiktok");
    }
  }, [location.search]);

  useEffect(() => {
    if (!vcTriggered && projectId) {
      if (isYoutube || isTikTok) {
        triggerVC(true);
        setVcTriggered(true);
      } else if (isDownload) {
        triggerVC(false);
        setVcTriggered(true);
      }
    }
  }, [isYoutube, isTikTok, isDownload, triggerVC, vcTriggered, projectId]);

  /**
   * To Update the Query params
   * @param {string} type 
   */
  const updateExportType = (type) => {
    setVcTriggered(false); // Reset flag when manually switching platforms
    const params = new URLSearchParams(location.search);
    params.set("export_type", type);
    history.push({
      pathname: location.pathname,
      search: params.toString(),
      state: location.state, // Preserve existing state
    });
  };

  /**
   * To remove the query params
   */
  const removeExportType = () => {
    const params = new URLSearchParams(location.search);
    params.delete("export_type");
    history.push({
      pathname: location.pathname,
      search: params.toString(),
      state: location.state, // Preserve existing state
    });
  };

  const handleUpgradeClick = (type) => {
    showPricingModal({ type, source: "Direct-download" });
  };

  const onDownloadClick = () => {
    if (isPaid && remainingCredits.export > 0) {
      updateExportType("download");
      // triggerVC(false);
    } else {
      handleUpgradeClick("download");
    }
    setStageClarity(TRACKERS.EXPORT_BTN_DOWNLOAD_CLICKED);
  };

  const onYouTubeClick = () => {
    if (!remainingCredits.social) {
      handleUpgradeClick("socialPublish")
    } else {
      setStageClarity(TRACKERS.EXPORT_BTN_YOUTUBE_CLICKED);
      updateExportType("youtube");
      // triggerVC(true);
    }
  };

  const onTikTokClick = () => {
    if (!remainingCredits.social) {
      handleUpgradeClick("socialPublish")
    } else {
      setStageClarity(TRACKERS.EXPORT_BTN_TIKTOK_CLICKED);
      updateExportType("tiktok");
      // triggerVC(true);
    }
  };

  const onGoBack = () => {
    if (isYoutube) {
      setIsYoutube(false);
    } else if (isTikTok) {
      setIsTikTok(false);
    } else {
      setIsDownload(false);
    }
    removeExportType();
  }

  if (isPublish) {
    return <Progress isTikTok={isTikTok} />;
  }

  if (isYoutube) {
    return <Youtube onBackAction={onGoBack} isShortsPlayer={isShortsPlayer} shortId={shortId} showWatermMark={showWatermMark} />
  }

  if (isTikTok) {
    return <Tiktok onBackAction={onGoBack} isShortsPlayer={isShortsPlayer} shortId={shortId} showWatermMark={showWatermMark} />
  }

  if (isDownload) {
    return <ExportDetails
      goBack={onGoBack}
      isShortsPlayer={isShortsPlayer} shortId={shortId}
    />
  }

  return (
    <ExportSidebar remainingCredits={remainingCredits.social !== 0 && totalCredits !== undefined} >
      <Typography
        content={content.EXPORT_VIDEO}
        color={vmTheme[theme].animoHeaderText}
        enableTrim={false}
        font={font.semiBold_45}
        display={"block"}
        align={"center"}
      />
      <Typography
        content={content.PUBLISH_TO_YOUTUBE}
        color={vmTheme[theme].exportLabelColor}
        enableTrim={false}
        font={font.normal14_400}
        display={"block"}
        align={"center"}
      />
      <Typography
        innerContent={totalCredits - usedCredits === 1 ? "Publish to (1 publish left)" : "Publish to "}
        color={vmTheme[theme].sbSecondaryColor}
        enableTrim={false}
        font={font.mediumBase_21}
        padding={"32px 0px 8px"}
      />
      {!isPaid && !showWatermMark ? <Celebration>
        <Typography
          content={content.CONGRATS_FREE_PUBLISH}
          color={vmTheme[theme].celebrationViolet}
          enableTrim={false}
          font={font.boldMini}
          display={"block"}
          width={"auto"}
          padding={"2px 0px 8px"}
        />
      </Celebration> : null}
      <Action
        text={content.YOUTUBE}
        fontColor={vmTheme[theme].paymentCardTextColor}
        background={vmTheme[theme].polarColor}
        height={"auto"}
        width={"100%"}
        icon={`${STATIC_PATH}vm-youtube.svg`}
        font={font.mediumBase_21}
        hoverColor={vmTheme[theme].polarColor}
        borderColor={vmTheme[theme].socialBorderColor}
        borderWidth={"1px"}
        borderRadius={"12px"}
        marginTop={"0px"}
        marginBottom={"8px"}
        customClass={"social-media"}
        top={"6px"}
        onClick={onYouTubeClick}
      >
        {(remainingCredits.social === 0 && totalCredits !== undefined) ? (
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginLeft: "auto" }}>
            <span>Upgrade</span>
            <img src={`${STATIC_PATH}yellow-star.svg`} alt="Star Icon" />
          </div>
        ) : null}

      </Action>
      <Action
        text={content.TIKTOK}
        fontColor={vmTheme[theme].paymentCardTextColor}
        background={vmTheme[theme].polarColor}
        height={"auto"}
        width={"100%"}
        icon={`${STATIC_PATH}tiktok.svg`}
        font={font.mediumBase_21}
        hoverColor={vmTheme[theme].polarColor}
        borderColor={vmTheme[theme].socialBorderColor}
        borderWidth={"1px"}
        borderRadius={"12px"}
        marginTop={"0px"}
        marginBottom={"8px"}
        customClass={"social-media tiktok"}
        top={"6px"}
        onClick={onTikTokClick}
      >
        {(remainingCredits.social === 0 && totalCredits !== undefined) ? (
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginLeft: "auto" }}>
            <span>Upgrade</span>
            <img src={`${STATIC_PATH}yellow-star.svg`} alt="Star Icon" />
          </div>
        ) : null}

      </Action>
      <Exceed
        showExceed={remainingCredits.social === 0 && totalCredits}
        content={`${totalCredits}/${usedCredits} publish Limit Exceeded`}
      />
      <Typography
        content={content.DIRECT_DOWNLOAD}
        color={vmTheme[theme].sbSecondaryColor}
        enableTrim={false}
        font={font.mediumBase_21}
        padding={"8px 0px"}
      />

      <Action
        text={isPaid ? content.DOWNLOAD_VIDEO : content.DOWNLOAD}
        fontColor={vmTheme[theme].paymentCardTextColor}
        background={vmTheme[theme].polarColor}
        height={"auto"}
        width={"100%"}
        icon={`${STATIC_PATH}${isPaid ? "download" : "download"}.svg`}
        font={font.mediumBase_21}
        hoverColor={vmTheme[theme].polarColor}
        borderColor={isPaid ? vmTheme[theme].socialBorderColor : vmTheme[theme].upgradeBorderColor}
        borderWidth={"1px"}
        borderRadius={"12px"}
        marginTop={"0px"}
        customClass={`social-media ${isPaid ? "" : "upgrade"}`}
        top={isPaid ? "6px" : "7px"}
        onClick={onDownloadClick}
      >
        {!isPaid || (remainingCredits.export === 0 && exportTotalCredits !== undefined) ? (
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginLeft: "auto" }}>
            <span>Upgrade</span>
            <img src={`${STATIC_PATH}yellow-star.svg`} alt="Star Icon" />
          </div>
        ) : null}
      </Action>
      <Exceed
        showExceed={remainingCredits.export === 0 && exportTotalCredits}
        content={`${exportTotalCredits}/${exportUsedCredits} download Limit Exceeded`}
      />
    </ExportSidebar>
  );
};

Publish.propTypes = {
  isShortsPlayer: PropTypes.bool,
  shortId: PropTypes.string,
  isPublish: PropTypes.bool
}

export default Publish;
